import { getErrorMessage } from "@captions-web/dev-logger";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { t } from "i18next";
import { z } from "zod";

import { MirageLogoLarge } from "~/components/mirage-logo";
import { NoiseGrid } from "~/components/noise-grid";
import { LinkButton } from "~/components/primitives/button";
import { SessionEvents } from "~/lib/analytics/defs/SessionEvents";
import { getAuthStore } from "~/modules/auth/auth-store";
import { LoginCredentialsType } from "~/modules/auth/credentials";
import { exchangeCodeForSessionMutation } from "~/modules/auth/network";
import { css, cx } from "~/styled-system/css";

export const Route = createFileRoute("/auth/callback")({
  component: RouteComponent,

  validateSearch: z.object({ code: z.string().optional(), state: z.string().optional() }),
  loaderDeps: ({ search }) => ({
    code: search.code,
    state: search.state,
  }),
  async loader({ deps: { code, state } }) {
    if (!code) {
      // if code is missing, just redirect to home
      throw redirect({ to: "/" });
    }

    let data: LoginCredentialsType;

    try {
      data = await exchangeCodeForSessionMutation.trigger({ params: { code, state } });
    } catch (error) {
      SessionEvents.login_callback_status.track({
        status: "fail",
        state,
        error_message: getErrorMessage(error),
      });

      return { error: "Failed to exchange code for session" };
    }

    getAuthStore().saveLoginCredentials(data);

    SessionEvents.login_callback_status.track({
      status: "success",
      state,
    });

    throw redirect({ to: "/home" });
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  return (
    <div className={cx(rootStyles, css({ animation: "fadeIn" }))}>
      <NoiseGrid />
      <div className={contentStyles}>
        <MirageLogoLarge className={css({ animation: "breath", color: "$gray.500!" })} />
        <h1 className={css({ textStyle: "heading-4" })}>
          {t("mirage-studio:auth.callback.loading-title")}
        </h1>
      </div>
    </div>
  );
}

function RouteComponent() {
  const { error } = Route.useLoaderData();

  if (error) {
    return (
      <div className={rootStyles}>
        <div className={contentStyles}>
          <MirageLogoLarge />
          <div className={css({ color: "$error" })}>{error}</div>
          <div className={interactiveStyles}>
            <LinkButton variant="secondary" to="/" size="md">
              Back to login
            </LinkButton>
          </div>
        </div>
      </div>
    );
  }
}

const rootStyles = css({
  display: "flex",
  height: "100dvh",
  width: "100%",
});

const contentStyles = css({
  position: "relative",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "12px",
  width: "100%",
  pointerEvents: "none",
  color: "$gray.200",

  zIndex: "$content",
});

const interactiveStyles = css({
  pointerEvents: "auto",
});
